import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTemplate,
  updateTemplate,
  duplicateTemplate,
  addSection,
  editSection,
  deleteSection,
  addField,
  editField,
  deleteField,
} from '../../services/eligibilityService';
import ConfirmModal from '../common/ConfirmModal';
import Loader from '../common/Loader';
import Alert from '../common/Alert';

const FIELD_TYPES = [
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'yes_no_na', label: 'Yes / No / N/A' },
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'timeline_task', label: 'Timeline Task' },
];

function generateId(label) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40) || `field_${Date.now()}`;
}

export default function EligibilityTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [saving, setSaving] = useState(false);

  // Header editing
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({ name: '', description: '', disability_category: '' });

  // Section state
  const [expandedSections, setExpandedSections] = useState({});
  const [addingSectionForm, setAddingSectionForm] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [sectionDraft, setSectionDraft] = useState({ name: '' });

  // Field state
  const [addingFieldToSection, setAddingFieldToSection] = useState(null);
  const [editingFieldKey, setEditingFieldKey] = useState(null);
  const [fieldDraft, setFieldDraft] = useState({ id: '', label: '', type: 'yes_no', help_text: '', due_days_before: '' });

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchTemplate = useCallback(async () => {
    try {
      const res = await getTemplate(templateId);
      setTemplate(res.data.data);
      const sMap = {};
      (res.data.data.sections || []).forEach(s => { sMap[s.id] = true; });
      setExpandedSections(sMap);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to load template.' });
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  const handleSaveHeader = async () => {
    setSaving(true);
    try {
      const body = {};
      if (headerDraft.name.trim()) body.name = headerDraft.name.trim();
      if (headerDraft.description !== undefined) body.description = headerDraft.description.trim();
      if (headerDraft.disability_category !== undefined) body.disability_category = headerDraft.disability_category.trim() || null;
      const res = await updateTemplate(templateId, body);
      setTemplate(res.data.data);
      setEditingHeader(false);
      setAlert({ type: 'success', message: 'Template updated.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to update template.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const res = await duplicateTemplate(templateId);
      setAlert({ type: 'success', message: 'Template duplicated.' });
      navigate(`/admin/eligibility-templates/${res.data.data.id}`);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to duplicate.' });
    }
  };

  // Section operations
  const handleAddSection = async () => {
    if (!addingSectionForm?.name?.trim()) return;
    setSaving(true);
    try {
      const sectionId = generateId(addingSectionForm.name);
      const order = (template.sections || []).length;
      const res = await addSection(templateId, { id: sectionId, name: addingSectionForm.name.trim(), order, fields: [] });
      setTemplate(res.data.data);
      setAddingSectionForm(null);
      setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to add section.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSection = async (sectionId) => {
    if (!sectionDraft.name?.trim()) return;
    setSaving(true);
    try {
      const res = await editSection(templateId, sectionId, { name: sectionDraft.name.trim() });
      setTemplate(res.data.data);
      setEditingSectionId(null);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to update section.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      const res = await deleteSection(templateId, deleteConfirm.id);
      setTemplate(res.data.data);
      setDeleteConfirm(null);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to delete section.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleMoveSection = async (sectionId, direction) => {
    const sections = [...(template.sections || [])].sort((a, b) => a.order - b.order);
    const idx = sections.findIndex(s => s.id === sectionId);
    if ((direction === 'up' && idx <= 0) || (direction === 'down' && idx >= sections.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    try {
      await editSection(templateId, sections[idx].id, { order: sections[swapIdx].order });
      await editSection(templateId, sections[swapIdx].id, { order: sections[idx].order });
      const res = await getTemplate(templateId);
      setTemplate(res.data.data);
    } catch (err) {
      setAlert({ type: 'error', message: 'Failed to reorder sections.' });
    }
  };

  // Field operations
  const handleAddField = async (sectionId) => {
    if (!fieldDraft.label?.trim()) {
      setAlert({ type: 'error', message: 'Field label is required.' });
      return;
    }
    setSaving(true);
    try {
      const fId = fieldDraft.id?.trim() || generateId(fieldDraft.label);
      const section = (template.sections || []).find(s => s.id === sectionId);
      const order = (section?.fields || []).length;
      const body = { id: fId, label: fieldDraft.label.trim(), type: fieldDraft.type, order };
      if (fieldDraft.help_text?.trim()) body.help_text = fieldDraft.help_text.trim();
      if (fieldDraft.type === 'timeline_task' && fieldDraft.due_days_before) {
        body.due_days_before = Number(fieldDraft.due_days_before);
      }
      const res = await addField(templateId, sectionId, body);
      setTemplate(res.data.data);
      setAddingFieldToSection(null);
      setFieldDraft({ id: '', label: '', type: 'yes_no', help_text: '', due_days_before: '' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to add field.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditField = async (sectionId, fieldId) => {
    if (!fieldDraft.label?.trim()) return;
    setSaving(true);
    try {
      const body = { label: fieldDraft.label.trim(), type: fieldDraft.type };
      if (fieldDraft.help_text !== undefined) body.help_text = fieldDraft.help_text.trim() || null;
      if (fieldDraft.type === 'timeline_task' && fieldDraft.due_days_before) {
        body.due_days_before = Number(fieldDraft.due_days_before);
      }
      const res = await editField(templateId, sectionId, fieldId, body);
      setTemplate(res.data.data);
      setEditingFieldKey(null);
      setFieldDraft({ id: '', label: '', type: 'yes_no', help_text: '', due_days_before: '' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to update field.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      const res = await deleteField(templateId, deleteConfirm.sectionId, deleteConfirm.id);
      setTemplate(res.data.data);
      setDeleteConfirm(null);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to delete field.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleMoveField = async (sectionId, fieldId, direction) => {
    const section = (template.sections || []).find(s => s.id === sectionId);
    if (!section) return;
    const fields = [...(section.fields || [])].sort((a, b) => a.order - b.order);
    const idx = fields.findIndex(f => f.id === fieldId);
    if ((direction === 'up' && idx <= 0) || (direction === 'down' && idx >= fields.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    try {
      await editField(templateId, sectionId, fields[idx].id, { order: fields[swapIdx].order });
      await editField(templateId, sectionId, fields[swapIdx].id, { order: fields[idx].order });
      const res = await getTemplate(templateId);
      setTemplate(res.data.data);
    } catch (err) {
      setAlert({ type: 'error', message: 'Failed to reorder fields.' });
    }
  };

  if (loading) return <Loader fullScreen text="Loading template..." />;
  if (!template) return <div className="p-6 text-center text-gray-500">Template not found.</div>;

  const sections = [...(template.sections || [])].sort((a, b) => a.order - b.order);

  return (
    <div>
      {alert && (
        <div className="fixed top-20 right-4 z-50 w-80 sm:w-96">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <ConfirmModal
        open={!!deleteConfirm}
        title={deleteConfirm?.type === 'section' ? 'Delete Section' : 'Delete Field'}
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This cannot be undone.`}
        onConfirm={deleteConfirm?.type === 'section' ? handleDeleteSection : handleDeleteField}
        onCancel={() => setDeleteConfirm(null)}
        confirmText="Delete"
        variant="danger"
        loading={deleteLoading}
      />

      {/* Page header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/admin/eligibility-templates')} className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="min-w-0">
            {!editingHeader ? (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">{template.name}</h1>
                  {template.is_default && <span className="px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-[10px] font-semibold uppercase">Default</span>}
                </div>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5 truncate">
                  {template.disability_category && <span className="capitalize">{template.disability_category} &middot; </span>}
                  {template.description || 'No description'}
                </p>
              </>
            ) : (
              <div className="space-y-2">
                <input value={headerDraft.name} onChange={e => setHeaderDraft(p => ({ ...p, name: e.target.value }))} placeholder="Template name" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                <input value={headerDraft.disability_category} onChange={e => setHeaderDraft(p => ({ ...p, disability_category: e.target.value }))} placeholder="Disability category (optional)" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                <textarea value={headerDraft.description} onChange={e => setHeaderDraft(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white resize-none" />
                <div className="flex gap-2">
                  <button onClick={handleSaveHeader} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">Save</button>
                  <button onClick={() => setEditingHeader(false)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
        {!editingHeader && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => { setHeaderDraft({ name: template.name, description: template.description || '', disability_category: template.disability_category || '' }); setEditingHeader(true); }} title="Edit template details" className="p-2 rounded-xl text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            <button onClick={handleDuplicate} title="Duplicate template" className="p-2 rounded-xl text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            </button>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {sections.map((section, sIdx) => (
          <div key={section.id} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
            {/* Section header */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 bg-gray-50 dark:bg-slate-750 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <button onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))} className="text-gray-400 hover:text-gray-700 dark:hover:text-white">
                  <svg className={`w-4 h-4 transition-transform ${expandedSections[section.id] ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {editingSectionId === section.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input value={sectionDraft.name} onChange={e => setSectionDraft({ name: e.target.value })} className="flex-1 px-2 py-1 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                    <button onClick={() => handleEditSection(section.id)} disabled={saving} className="px-3 py-1 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50">Save</button>
                    <button onClick={() => setEditingSectionId(null)} className="px-3 py-1 rounded border border-gray-300 dark:border-slate-600 text-xs text-gray-600 dark:text-slate-300">Cancel</button>
                  </div>
                ) : (
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-200 truncate">{section.name}</h3>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleMoveSection(section.id, 'up')} disabled={sIdx === 0} title="Move up" className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-white disabled:opacity-30">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                </button>
                <button onClick={() => handleMoveSection(section.id, 'down')} disabled={sIdx === sections.length - 1} title="Move down" className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-white disabled:opacity-30">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                <button onClick={() => { setEditingSectionId(section.id); setSectionDraft({ name: section.name }); }} title="Edit name" className="p-1 rounded text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
                <button onClick={() => setDeleteConfirm({ type: 'section', id: section.id, name: section.name })} title="Delete section" className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>
            </div>

            {/* Section fields */}
            {expandedSections[section.id] && (
              <div className="px-4 sm:px-5 py-3 space-y-2">
                {[...(section.fields || [])].sort((a, b) => a.order - b.order).map((field, fIdx) => (
                  <div key={field.id} className="flex items-start justify-between gap-2 p-3 rounded-xl bg-gray-50 dark:bg-slate-750 border border-gray-100 dark:border-slate-700">
                    {editingFieldKey === `${section.id}/${field.id}` ? (
                      <div className="flex-1 space-y-2">
                        <input value={fieldDraft.label} onChange={e => setFieldDraft(p => ({ ...p, label: e.target.value }))} placeholder="Field label" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                        <div className="flex gap-2 flex-wrap">
                          <select value={fieldDraft.type} onChange={e => setFieldDraft(p => ({ ...p, type: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white">
                            {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                          </select>
                          <input value={fieldDraft.help_text} onChange={e => setFieldDraft(p => ({ ...p, help_text: e.target.value }))} placeholder="Help text (optional)" className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                          {fieldDraft.type === 'timeline_task' && (
                            <input type="number" value={fieldDraft.due_days_before} onChange={e => setFieldDraft(p => ({ ...p, due_days_before: e.target.value }))} placeholder="Due days before" className="w-32 px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleEditField(section.id, field.id)} disabled={saving} className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50">Save</button>
                          <button onClick={() => { setEditingFieldKey(null); setFieldDraft({ id: '', label: '', type: 'yes_no', help_text: '', due_days_before: '' }); }} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 text-xs text-gray-600 dark:text-slate-300">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-800 dark:text-slate-200">{field.label}</span>
                            <span className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-600 text-[10px] text-gray-600 dark:text-slate-300 font-mono">{field.type}</span>
                          </div>
                          {field.help_text && <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{field.help_text}</p>}
                          <p className="text-[10px] text-gray-300 dark:text-slate-600 mt-0.5 font-mono">id: {field.id}</p>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button onClick={() => handleMoveField(section.id, field.id, 'up')} disabled={fIdx === 0} title="Move up" className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-white disabled:opacity-30">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                          </button>
                          <button onClick={() => handleMoveField(section.id, field.id, 'down')} disabled={fIdx === (section.fields || []).length - 1} title="Move down" className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-white disabled:opacity-30">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                          </button>
                          <button onClick={() => { setEditingFieldKey(`${section.id}/${field.id}`); setFieldDraft({ id: field.id, label: field.label, type: field.type, help_text: field.help_text || '', due_days_before: field.due_days_before || '' }); }} title="Edit" className="p-1 rounded text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => setDeleteConfirm({ type: 'field', id: field.id, sectionId: section.id, name: field.label })} title="Delete" className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Add field form */}
                {addingFieldToSection === section.id ? (
                  <div className="p-3 rounded-xl border-2 border-dashed border-indigo-300 dark:border-indigo-600 space-y-2">
                    <div className="flex gap-2 flex-wrap">
                      <input value={fieldDraft.label} onChange={e => setFieldDraft(p => ({ ...p, label: e.target.value }))} placeholder="Field label *" className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                      <select value={fieldDraft.type} onChange={e => setFieldDraft(p => ({ ...p, type: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white">
                        {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <input value={fieldDraft.id} onChange={e => setFieldDraft(p => ({ ...p, id: e.target.value }))} placeholder="Field ID (auto-generated if empty)" className="flex-1 min-w-[150px] px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white font-mono" />
                      <input value={fieldDraft.help_text} onChange={e => setFieldDraft(p => ({ ...p, help_text: e.target.value }))} placeholder="Help text (optional)" className="flex-1 min-w-[150px] px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                      {fieldDraft.type === 'timeline_task' && (
                        <input type="number" value={fieldDraft.due_days_before} onChange={e => setFieldDraft(p => ({ ...p, due_days_before: e.target.value }))} placeholder="Due days before" className="w-36 px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleAddField(section.id)} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50">Add Field</button>
                      <button onClick={() => { setAddingFieldToSection(null); setFieldDraft({ id: '', label: '', type: 'yes_no', help_text: '', due_days_before: '' }); }} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-xs text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAddingFieldToSection(section.id); setFieldDraft({ id: '', label: '', type: 'yes_no', help_text: '', due_days_before: '' }); }}
                    className="w-full py-2 rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-700 text-sm text-gray-400 dark:text-slate-500 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    + Add Field
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add section */}
        {addingSectionForm ? (
          <div className="bg-white dark:bg-slate-800 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <input
                value={addingSectionForm.name}
                onChange={(e) => setAddingSectionForm({ name: e.target.value })}
                placeholder="Section name"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white"
                autoFocus
              />
              <button onClick={handleAddSection} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">Add</button>
              <button onClick={() => setAddingSectionForm(null)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">Cancel</button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAddingSectionForm({ name: '' })}
            className="w-full py-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-400 dark:text-slate-500 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            + Add Section
          </button>
        )}
      </div>
    </div>
  );
}
